import React from "react";
import { houseEmoji } from "@/lib/houseEmoji";
import { isHouseUnlocked } from "@/lib/houseLock";
import { Lock } from "lucide-react";

/**
 * HouseTiles — the "which house?" front screen shown on the dashboard
 * when a family uses the Houses feature. Each tile is a doorbell:
 * a big emoji + the house name. Tapping fires `onEnter(house)`, and
 * the caller decides whether to ask for an unlock PIN, prompt to
 * create one, or just walk in.
 *
 * Above the grid we show a "Shared" row — members whose
 * `cross_house_privacy` lets the other house see their status. Those
 * members are visible to everyone on the family device without needing
 * a house PIN, so they live on the front rather than behind a door.
 */
export default function HouseTiles({
  houses,
  sharedMembers,
  memberCounts,
  onEnter,
  renderSharedTile,
}) {
  const sorted = [...houses].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className="space-y-8" data-testid="house-tiles-front">
      {sharedMembers.length > 0 && (
        <section data-testid="house-tiles-shared">
          <div className="label-tiny uppercase tracking-wider text-[color:var(--ink-3)] mb-3">
            Shared · {sharedMembers.length}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
            {sharedMembers.map((m, i) => renderSharedTile(m, i))}
          </div>
        </section>
      )}

      <section>
        <div className="label-tiny uppercase tracking-wider text-[color:var(--ink-3)] mb-3">
          Houses · {sorted.length}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {sorted.map((h, i) => {
            const locked = h.has_pin && !isHouseUnlocked(h.id);
            const needsSetup = !h.has_pin;
            const count = memberCounts[h.id] || 0;
            return (
              <button
                key={h.id}
                type="button"
                onClick={() => onEnter(h)}
                className="rounded-[18px] p-5 md:p-6 flex items-center gap-4 text-left bg-[color:var(--surface)] border border-[color:var(--line-soft)] hover:border-[color:var(--ink-3)] hover:scale-[1.01] active:scale-[0.99] transition-all"
                data-testid={`house-tile-${h.id}`}
              >
                <span
                  className="text-5xl md:text-6xl leading-none flex-shrink-0"
                  aria-hidden="true"
                >
                  {houseEmoji(i)}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display-italic text-2xl md:text-3xl leading-tight tracking-tight">
                    {h.name}
                  </h3>
                  <div className="label-tiny text-[color:var(--ink-3)] mt-1 flex items-center gap-2 flex-wrap">
                    <span>{count} inside</span>
                    <span aria-hidden="true">·</span>
                    <span>
                      tap to {needsSetup ? "set up" : locked ? "unlock" : "enter"}
                    </span>
                  </div>
                </div>
                {locked && (
                  <span
                    className="flex items-center gap-1 label-tiny text-[color:var(--ink-3)] flex-shrink-0"
                    data-testid={`house-tile-locked-${h.id}`}
                  >
                    <Lock className="w-3 h-3" strokeWidth={2} />
                    Locked
                  </span>
                )}
                {needsSetup && (
                  <span
                    className="label-tiny text-[color:var(--ink-3)] flex-shrink-0"
                    data-testid={`house-tile-setup-${h.id}`}
                  >
                    Set PIN
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
